import type { ISbStoriesParams, StoryblokBridgeConfigV2 } from '@storyblok/vue'
import { defu } from 'defu'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import { isEmpty, pick } from 'lodash-es'
import type { ISbComponentType, ISbStoryData } from 'storyblok-js-client'

type DefaultContentType = ISbComponentType<string> & { [index: string]: any }

type UsePageOptions<T = DefaultContentType> = {
  apiOptions?: ISbStoriesParams
  bridgeOptions?: StoryblokBridgeConfigV2
  asyncOptions?: NitroFetchOptions<ISbStoryData<T>>
  request?: NitroFetchRequest
}

function hasMetadata(obj: Record<string, any>) {
  const defaultMetadata = pick(obj, ['title', 'description'])
  const ogMetadata = pick(obj, ['og_title', 'og_description'])

  return Object.values(defaultMetadata).some(value => !isEmpty(value)) || Object.values(ogMetadata).some(value => !isEmpty(value))
}

const usePageState = <T = DefaultContentType>(uniqueKey: string) => useState<ISbStoryData<T>>(`${uniqueKey}-state`)

export const usePage = async <T = DefaultContentType>(options?: UsePageOptions<T>) => {
  const seoState = useSeoState()
  const { page: pageKey } = useDataKey()
  const route = useRoute()
  const apiOptions = options?.apiOptions ?? {}
  const bridgeOptions = options?.bridgeOptions ?? {}
  const asyncOptions = options?.asyncOptions ?? {}
  const request = options?.request ?? null
  const currentPath = ref<NitroFetchRequest>('')

  if (request) {
    currentPath.value = request
  }
  else {
    currentPath.value = route.path === '/' || route.path === '' ? '/home' : route.path
  }

  const params = defu(apiOptions, {})
  const uniqueKey = computed(() => pageKey(undefined, {
    params,
  }) /* String(`${JSON.stringify(params)}${currentPath.value}`) */)
  const nuxtData = useNuxtData(uniqueKey.value)
  const stateData = usePageState<T>(uniqueKey.value)
  const story = ref(stateData.value || nuxtData.data.value || null)

  const metadata = computed(() => story.value?.content?.metadata ?? {})

  function fetchStory() {
    return $fetch<ISbStoryData<T>>(`/api${currentPath.value}`, {
      ...asyncOptions,
      params,
    })
  }

  function setMetadata() {
    if (metadata.value && tryUseNuxtApp()) {
      if (hasMetadata(metadata.value)) {
        useSeoMeta({
          title: () => metadata.value?.title,
          description: () => metadata.value.description || metadata.value.og_description,
          ogTitle: () => metadata.value.og_title || metadata.value?.title,
          ogDescription: () => metadata.value.og_description || metadata.value.description,
        })

        useHead({
          title: () => metadata.value?.title,
          templateParams: {
            category: null,
          },
        })
      }
    }
  }

  onMounted(() => {
    if (tryUseNuxtApp()?.$preview && story.value && story.value.id) {
      useStoryblokBridge(
        story.value.id,
        evStory => {
          console.log(`Storyblok bridge: story updated for ${evStory}`)
          story.value = evStory
        },
        {
          ...bridgeOptions,
          preventClicks: true,
        },
      )
    }
  })

  if (!story.value) {
    const data = await fetchStory()

    // if (error.value?.statusCode && error.value?.statusCode >= 400 && error.value?.statusCode < 600) {
    //   throw createError({
    //     statusCode: error.value?.statusCode,
    //     statusMessage: error.value?.statusMessage,
    //   })
    // }

    if (data) {
      story.value = data
    }
  }

  watch(currentPath, () => {
    // setMetadata()
    seoState.value = metadata.value
  }, {
    immediate: true,
  })

  return {
    story,
    uniqueKey,
    fetchStory,
  }
}
